/**
 * Items lying on the floor, and the egg of a defeated boss, as camera-facing
 * pixel billboards at both tiers (integration plan slice 2b; the Rendering
 * page's sprite rule, the path slice 3's sprites follow): one instanced quad
 * per item, textured from the pattern atlas (`item-patterns.ts`) with nearest
 * filtering and a hard alpha cut, never `THREE.Sprite`. At Medium they cast a
 * cut-out shadow through a `customDepthMaterial` carrying the same atlas and
 * cut. An item is owned by its tile (`item:x,y`), as the box markers were, so
 * `item_picked_up` hides it and `item_left` brings it back and bobs it. At Off
 * they are unlit and darken toward the fog per instance (the prototype's fogged
 * pixels); at Medium they take the lit material with the baked light and the
 * shader's fog mask, as the proxies' successors (slice 3's sprites) will.
 * The last instance is the egg, hidden until `egg_obtained` plays.
 */
import type { ItemIcon, Theme } from '@ab/content-schema';
import type { Floor } from '@ab/game-core';
import * as THREE from 'three';
import { presentation } from './constants.ts';
import { tileIndex } from './grid.ts';
import { lookOrder, patternAtlas, patternPresentation } from './item-patterns.ts';
import { itemOwner } from './markers.ts';
import { type ShaderHook, addShaderHook } from './material-hooks.ts';
import { billboardUv, billboardVertexHeader } from './shaders/billboard.ts';

export type Billboards = {
  readonly mesh: THREE.InstancedMesh;
  setHidden(owner: string, hidden: boolean): void;
  setOffset(owner: string, dy: number): void;
  setEgg(at: { readonly x: number; readonly y: number; readonly lift: number } | null): void;
  /** Faces every billboard to the camera and fades unseen ones; call before each frame. */
  update(camera: THREE.Camera, seen: (tile: number) => boolean, fog: THREE.Color): void;
  setTier(medium: boolean): void;
  /** Item billboards not picked up. */
  itemsShown(): number;
  dispose(): void;
};

type Slot = { readonly owner: string; readonly tile: number; x: number; y: number; dy: number; hidden: boolean };

const p = patternPresentation;

/**
 * The billboards of a floor's items; `icons` names each item's pattern (from its content record); an item
 * without one is an error (the content schema requires it). `lit` is the Medium tier's light and fog hook.
 */
export const buildBillboards = (floor: Floor, theme: Theme, icons: Readonly<Record<string, ItemIcon>>, lit: ShaderHook): Billboards => {
  const atlas = patternAtlas(theme);
  const size = p.pixels * p.pixel;
  const geometry = new THREE.PlaneGeometry(size, size);
  const slots: Slot[] = floor.items.map((item) => ({ owner: itemOwner(item.pos), tile: tileIndex(floor, item.pos.x, item.pos.y), x: item.pos.x, y: item.pos.y, dy: 0, hidden: false }));
  const egg: Slot = { owner: 'egg', tile: -1, x: 0, y: 0, dy: 0, hidden: true };
  slots.push(egg);
  const lookOf = (item: string): number => {
    const icon = Object.hasOwn(icons, item) ? icons[item] : undefined;
    if (icon === undefined) throw new Error(`the item ${item} has no icon to draw on the floor (show's itemIcons)`);
    return lookOrder.indexOf(icon);
  };
  geometry.setAttribute('abLook', new THREE.InstancedBufferAttribute(Float32Array.from([...floor.items.map((i) => lookOf(i.item)), lookOrder.indexOf('egg')]), 1));
  const looks = { value: lookOrder.length };
  const hook = { key: 'item-billboard', uniforms: { abLooks: looks }, vertexHeader: billboardVertexHeader, vertex: [{ chunk: 'uv_vertex' as const, after: billboardUv }] };
  // Off: unlit, fogged per instance. Medium: lit by the key light, the fill and the baked volume, fogged by the
  // exploration mask in the shader, like every other lit surface (the Rendering page's sprite rule).
  const material = new THREE.MeshBasicMaterial({ map: atlas, alphaTest: p.alphaTest, side: THREE.DoubleSide });
  addShaderHook(material, hook);
  const litMaterial = new THREE.MeshStandardMaterial({ map: atlas, alphaTest: p.alphaTest, side: THREE.DoubleSide, roughness: 1, metalness: 0 });
  addShaderHook(litMaterial, hook);
  addShaderHook(litMaterial, lit);
  let medium = false;
  const depth = new THREE.MeshDepthMaterial({ depthPacking: THREE.RGBADepthPacking, map: atlas, alphaTest: p.alphaTest, side: THREE.DoubleSide });
  addShaderHook(depth, hook);
  const mesh = new THREE.InstancedMesh<THREE.BufferGeometry, THREE.Material>(geometry, material, slots.length);
  mesh.name = 'item-billboards';
  mesh.frustumCulled = false;
  mesh.customDepthMaterial = depth;
  mesh.setColorAt(0, new THREE.Color(1, 1, 1));
  const m = new THREE.Matrix4();
  const v = new THREE.Vector3();
  const s = new THREE.Vector3();
  const c = new THREE.Color();
  const white = new THREE.Color(1, 1, 1);
  const { markerFade, markerDarken } = presentation.fog;
  const forOwner = (owner: string, change: (slot: Slot) => void): void => {
    for (const slot of slots) if (slot.owner === owner) change(slot);
  };
  return {
    mesh,
    setHidden(owner, hidden) {
      forOwner(owner, (slot) => {
        slot.hidden = hidden;
      });
    },
    setOffset(owner, dy) {
      forOwner(owner, (slot) => {
        slot.dy = dy;
      });
    },
    setEgg(at) {
      egg.hidden = at === null;
      if (at !== null) {
        egg.x = at.x;
        egg.y = at.y;
        egg.dy = at.lift;
      }
    },
    update(camera, seen, fog) {
      slots.forEach((slot, i) => {
        s.setScalar(slot.hidden ? 0 : 1);
        mesh.setMatrixAt(i, m.compose(v.set(slot.x, p.centreY + slot.dy, -slot.y), camera.quaternion, s));
        const unseen = !medium && slot.tile >= 0 && !seen(slot.tile);
        mesh.setColorAt(i, unseen ? c.copy(white).lerp(fog, markerFade).multiplyScalar(markerDarken) : white);
      });
      mesh.instanceMatrix.needsUpdate = true;
      if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    },
    setTier(m) {
      medium = m;
      mesh.castShadow = m;
      mesh.receiveShadow = m;
      mesh.material = m ? litMaterial : material;
    },
    itemsShown: () => slots.filter((s) => s !== egg && !s.hidden).length,
    dispose() {
      mesh.removeFromParent();
      mesh.dispose();
      geometry.dispose();
      material.dispose();
      litMaterial.dispose();
      depth.dispose();
      atlas.dispose();
    },
  };
};
